import type { CartItem, GiftcardCartItem } from '~/composables/types/cart'
import type {
  OrderCreationEventItem,
  OrderCreationGiftCardItem,
} from '~/composables/types/checkout'
import type { Pricings } from '~/composables/types/event'
import { KONFETTI_GIFTCARD_SUPPLIER_ID } from '~/utils/konfettiCheckoutConstants'

export type TicketCategoryLine = {
  category: string
  amount: number
}

export function ticketCategoriesFromPricings(
  pricings: Pricings | undefined
): TicketCategoryLine[] {
  return (
    pricings?.price_breakdown?.map((breakdown) => ({
      category: breakdown.title,
      amount: breakdown.total_participants,
    })) || []
  )
}

/** Same cents logic as legacy cart/order mapping (value-based vs event-based gift cards). */
export function getGiftcardLineValueInCents(item: GiftcardCartItem): number {
  const isValueBased =
    item.type === 'GIFT_CARD' && !item.id && !item.eventDescriptionId

  return isValueBased
    ? parseInt(
        item.pricings?.price_breakdown[0]?.price_per_person?.amount || '0'
      )
    : parseInt(item.pricings?.price_breakdown[0]?.total_price?.amount || '0') /
        item.quantity
}

/**
 * Supplier id for booking / embedded when the gift card line has no `supplierId`
 * (embedded query wins, then subdomain supplier fetch).
 */
export function resolveBookingGiftcardSupplierContext(args: {
  embeddedSupplierId?: string | string[] | null
  subdomainSupplierId?: string | null
}): string {
  const embedded = args.embeddedSupplierId
  if (embedded) {
    const v = Array.isArray(embedded) ? embedded[0] : embedded
    if (v) return String(v)
  }
  if (args.subdomainSupplierId) {
    return String(args.subdomainSupplierId)
  }
  return KONFETTI_GIFTCARD_SUPPLIER_ID
}

export type BuildEventCheckoutLineOptions = {
  /** When true, include total price in cents (same as place-order payload). */
  includePrice: boolean
}

/** Shared EVENT line for place-order and cart/validate. */
export function buildEventCheckoutLine(
  item: CartItem,
  options: BuildEventCheckoutLineOptions
): OrderCreationEventItem & { event?: string; date?: string } {
  if (item.type !== 'EVENT') {
    throw new Error('buildEventCheckoutLine: item must be EVENT')
  }

  const ticketCategories = ticketCategoriesFromPricings(item.pricings)
  const line: OrderCreationEventItem & { event?: string; date?: string } = {
    type: 'EVENT',
    product: item.id,
    quantity: item.quantity,
    private: item.private,
    ...(item.instantBooking && {
      event: item.eventDescriptionId,
      date: item.date,
    }),
    ...(ticketCategories.length > 0 && {
      ticket_categories: ticketCategories,
    }),
  }

  if (options.includePrice) {
    line.price = parseInt(item.pricings?.price?.total_price?.amount || '0')
  }

  return line
}

export type BuildGiftCardCheckoutLineOptions = {
  /** Booking solution or embedded checkout — always attach `supplier_id`. */
  external: boolean
  /** When `external`, used if the cart line has no `supplierId`. */
  bookingSupplierFallback?: string
}

/**
 * Shared GIFT_CARD line for place-order and cart/validate.
 * - Marketplace (`external: false`): no `supplier_id`; `event` is cart line `id` (same as orders).
 * - Booking / embedded (`external: true`): always `supplier_id`; ship/ship_type match orders.
 */
export function buildGiftCardCheckoutLine(
  item: GiftcardCartItem,
  options: BuildGiftCardCheckoutLineOptions
): OrderCreationGiftCardItem {
  const addon = item.giftcardAddon
  const ship = Boolean(addon) && addon !== 'PDF'

  const line: OrderCreationGiftCardItem = {
    type: 'GIFT_CARD',
    event: item.id,
    quantity: item.quantity,
    value: getGiftcardLineValueInCents(item),
    ship,
    ...(addon && addon !== 'PDF' ? { ship_type: addon } : {}),
    ...(item.split !== undefined ? { split: item.split } : {}),
  }

  if (options.external) {
    line.supplier_id =
      item.supplierId ||
      options.bookingSupplierFallback ||
      KONFETTI_GIFTCARD_SUPPLIER_ID
  }

  return line
}
